import { HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiService } from './api.service';

/**
 * Request payload for `POST /Lifecycle/get-workspace-license-details`.
 *
 * Aligned with the Reports/Documents grids — SAME wire param names AND data types
 * (multi-selects as id arrays, dates as ISO `yyyy-MM-dd` or null, `role` = the
 * logged-in user's *system* role, `role_flag` = the Role *filter* P/R) — plus the
 * Workspace-only `document_status` filter. The backend DTO is updated by the API
 * team to accept this shape (it previously used `flag`/`click_change_flag` and CSV
 * single-id fields). See
 * `docs/superpowers/specs/2026-06-29-workspace-grid-api-wiring-design.md`.
 */
export interface LicenseDetailsParams {
  /** Customer scope (UserService.getCustomerId()). */
  customer_id: number;
  /** Acting user (UserService.getUserId()). */
  user_id: number;
  /** `'S'` = Statutory, `'I'` = Internal. */
  is_statutory_or_internal: string;
  /** Logged-in user's *system* role (`UserService.getRole()`), NOT the Role filter. */
  role: string;
  /** Role *filter* selection: 'P' = Performer, 'R' = Reviewer (null = unselected). */
  role_flag: string | null;
  /** Entity/Sub-Entity/Location tree node ids. */
  entity_ids: string[];
  /** Selected status values (multiselect). */
  status_list: string[];
  /** Selected license-type values (multiselect). */
  license_type_list: string[];
  /** Selected user values (multiselect). */
  user_list: string[];
  /** Selected department values (multiselect). */
  department_list: string[];
  /** Inclusive start-date boundary as `dd-MM-yyyy`, or null. */
  start_date: string | null;
  /** Inclusive end-date boundary as `dd-MM-yyyy`, or null. */
  end_date: string | null;
  /**
   * INCLUSIVE lower bound on the licence's own end-date, `dd-MM-yyyy`, or null — the
   * counterpart of {@link end_date}'s upper cap. A drill-scope-only field, never a
   * user-facing filter control: set only by the Expiry Timeline bucket drill
   * (`ManagementDashboardComponent.onExpiryBucketSelect` → `floorEndDate`), never patched
   * from the Workspace filter bar. Applied in memory over the stored procedure's rows on
   * the backend (no stored procedure change — rule 204's third bullet).
   */
  end_date_from: string | null;
  /** Free-text search term. */
  search_text: string;
  /** Selected Document Status ('all'/'submitted'/'pending'); null = unselected. */
  document_status: string | null;
  /** Term scope: 'Perpetual' | 'Fixed-Term' (the backend-confirmed spelling, matching the
      is_permanent_active row domain — user 2026-08-19), or null = All (no narrowing). Backend SP
      owns the predicate over the dirty is_permanent_active column — see the 2026-08-19
      term-scope ADR. */
  license_term: 'Perpetual' | 'Fixed-Term' | null;
}

/**
 * One license record as returned by `POST /Lifecycle/get-workspace-license-details`
 * (the backend `LicenseDetailsDTO`). Only the fields the Workspace grid consumes — plus
 * row identifiers carried for a future View/Edit task — are typed; the record
 * carries many more (mgr_status, status_result, customer_branch_id, …).
 *
 * Read the list tolerant of `data`/`Data` casing (see {@link LicenseDetailsResponse}):
 * per this repo's history the backend's casing varies per endpoint and a
 * wrong-case read silently yields `undefined`.
 */
export interface LicenseDetail {
  /** Branch/location name shown in the grid's Location column. */
  customer_branch?: string;
  /** License registration number. */
  license_no?: string;
  /** License title/description. */
  license_title?: string;
  /** Name of the user responsible for performing the compliance. */
  performer_name?: string;
  /** Name of the user responsible for reviewing the compliance. */
  reviewer_name?: string;
  /** Application date (ISO datetime); not currently rendered as a column. */
  application_date?: string;
  /** License end/expiry date (ISO datetime), formatted for the End Date column. */
  end_date?: string;
  /** Compliance status label. NOT rendered — see {@link mgr_status}. */
  status?: string;
  /**
   * ASPX `MGRStatus` — manager/compliance status. Drives action-icon visibility AND is the
   * value the Workspace grid's Status column renders: the workflow status is the state the
   * page is about, so the column and the icon rules must read the same field or an icon can
   * contradict the status next to it. `status` is deliberately not shown.
   */
  mgr_status?: string;
  /** ASPX `StatusResult` — server flag "this performer/reviewer may act on the row". */
  status_result?: boolean;
  /** ASPX `EnableEdit` — TRENT-only per-row edit gate. Pending DB confirmation (see plan Task 6). */
  enable_edit?: boolean;
  /** Backend perpetual flag, mapped to the Perpetual Yes/No column. */
  is_permanent_active?: string;
  /** Identifies the license for the View/Edit action; not rendered as a column. */
  license_id?: number;
  /** Compliance instance id; carried for View/Edit; not a column. */
  compliance_instance_id?: number;
  /** Compliance schedule-on id; carried for View/Edit; not a column. */
  compliance_schedule_on_id?: number;
  /** License type display name. */
  license_type_name?: string;
  /** Department name (nullable on the wire). */
  dept_name?: string;
  /** License start date (ISO datetime). */
  start_date?: string;
  /** Branch/location id. */
  customer_branch_id?: number;
  /** Department id. */
  department_id?: number;
  /** License type id. */
  license_type_id?: number;
  /** Status id. */
  status_id?: number;
  /** Compliance id. */
  compliance_id?: number;
  /** Nominee name (nullable on the wire). */
  nominee_name?: string;
  /** Document status label. */
  document_status?: string;
  /**
   * Compliance category name, e.g. `'Uncategorized'` (nullable/absent on the wire).
   * Rendered by the Workspace grid's optional "Category" column, which ships HIDDEN and is
   * turned on from the column chooser.
   */
  compliance_category_name?: string;
}

/**
 * Envelope for the get-workspace-license-details response: the shared `{ success, message,
 * data, trace_id }` shape, the list under `data` (or PascalCase `Data` —
 * defensive, since casing varies per endpoint).
 */
export interface LicenseDetailsResponse {
  /** Whether the backend reported the request as successful. */
  success?: boolean;
  /** Human-readable status/result message from the backend. */
  message?: string;
  /** License list (snake_case casing). */
  data?: LicenseDetail[];
  /** License list (PascalCase casing) — read defensively as endpoint casing varies. */
  Data?: LicenseDetail[];
  /** Backend trace/correlation id for diagnostics. */
  trace_id?: string;
}

/**
 * API surface for the Workspace license grid, served by the backend `Lifecycle`
 * controller. (The class name predates the backend's move of these two endpoints
 * off the `Dashboard` controller; the other dashboard endpoints live in
 * `LicenseDashboardService`.)
 *
 * Naming convention (matches the other API services): each method name is the
 * camelCase form of the endpoint path
 * (`/Lifecycle/get-workspace-license-details` → `getLicenseDetails`).
 */
@Injectable({ providedIn: 'root' })
export class DashboardService {
  private readonly api = inject(ApiService);
  private readonly apiBaseUrl = environment.API_BASE_URL;

  /**
   * Fetches the Workspace license grid rows
   * (`/Lifecycle/get-workspace-license-details`) via the shared signed-request
   * helper (`ApiService.post`), which attaches the bearer token and
   * `X-Payload-HMAC`.
   * @param params Filter payload assembled from the Workspace filter bar.
   * @returns Observable emitting the HTTP response containing the license list.
   */
  getLicenseDetails(
    params: LicenseDetailsParams,
  ): Observable<HttpResponse<LicenseDetailsResponse>> {
    const url = `${this.apiBaseUrl}/Lifecycle/get-workspace-license-details`;
    return this.api.post<LicenseDetailsResponse>(url, params);
  }

  /**
   * Exports the Workspace license grid as an `.xlsx` file
   * (`/Lifecycle/workspace-export-excel`). Sends the SAME
   * {@link LicenseDetailsParams} payload the grid uses ({@link getLicenseDetails})
   * so the export always reflects the grid's current filters. Uses
   * {@link ApiService.downloadBlob}, which attaches the bearer token +
   * `X-Payload-HMAC` and returns the file as a blob response.
   *
   * On failure the backend may reply `200` with a JSON/text error body rather than
   * a workbook; the caller must inspect the content type before saving. Mirrors
   * `LicenseReportService.exportExcel` (the Reports-page export).
   * @param params Filter payload identical to the grid's (grid-identical export).
   * @returns Observable emitting the HTTP blob response (`.xlsx` on success).
   */
  workspaceExportExcel(params: LicenseDetailsParams): Observable<HttpResponse<Blob>> {
    const url = `${this.apiBaseUrl}/Lifecycle/workspace-export-excel`;
    return this.api.downloadBlob(url, params);
  }
}
